import React, { useEffect, useState } from "react";
import {
	getAdminProducts,
	deleteProductById,
	updateProductById,
	uploadProductImage,
	createProduct,
	getCategories,
} from "../../services/adminService";

const initialFormState = {
	name: "",
	slug: "",
	category_id: "",
	short_description: "",
	description: "",
	price: "",
	stock: "",
	available: true,
	featured: false,
	image_url: "",
};

// ── Auto-generate slug from name ──────────────────────────────────
const toSlug = (str) =>
	str
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9\s-]/g, "")
		.trim()
		.replace(/\s+/g, "-");

// ── Status badge ──────────────────────────────────────────────────
const StatusBadge = ({ available, stock }) => {
	if (!available)
		return (
			<span style={badge("red")}>
				<Dot color="#A32D2D" /> Inactif
			</span>
		);
	if (stock === 0)
		return (
			<span style={badge("amber")}>
				<Dot color="#854F0B" /> Épuisé
			</span>
		);
	return (
		<span style={badge("green")}>
			<Dot color="#3B6D11" /> Actif
		</span>
	);
};

const Dot = ({ color }) => (
	<span
		style={{
			display: "inline-block",
			width: 6,
			height: 6,
			borderRadius: "50%",
			background: color,
			marginRight: 5,
			flexShrink: 0,
		}}
	/>
);

const badge = (color) => {
	const map = {
		green: { bg: "#EAF3DE", text: "#3B6D11" },
		amber: { bg: "#FAEEDA", text: "#854F0B" },
		red: { bg: "#FCEBEB", text: "#A32D2D" },
	};
	return {
		display: "inline-flex",
		alignItems: "center",
		padding: "3px 10px",
		borderRadius: 20,
		fontSize: 12,
		fontWeight: 500,
		background: map[color].bg,
		color: map[color].text,
		whiteSpace: "nowrap",
	};
};

// ── Image thumbnail ───────────────────────────────────────────────
const ProductThumb = ({ src, alt }) =>
	src ? (
		<img
			src={src}
			alt={alt}
			style={{
				width: 40,
				height: 40,
				borderRadius: 8,
				objectFit: "cover",
				border: "0.5px solid #E6E1D6",
				display: "block",
			}}
		/>
	) : (
		<div
			style={{
				width: 40,
				height: 40,
				borderRadius: 8,
				background: "#F5F3EE",
				border: "0.5px solid #E6E1D6",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				color: "#B4B2A9",
				fontSize: 18,
			}}
		>
			☕
		</div>
	);

// ── Form field ────────────────────────────────────────────────────
const Field = ({ label, children, required }) => (
	<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
		<label
			style={{
				fontSize: 12,
				fontWeight: 500,
				color: "#6B6B6B",
				letterSpacing: "0.04em",
			}}
		>
			{label}
			{required && <span style={{ color: "#E24B4A", marginLeft: 2 }}>*</span>}
		</label>
		{children}
	</div>
);

const inputStyle = {
	border: "0.5px solid #D3D1C7",
	borderRadius: 10,
	padding: "9px 12px",
	fontSize: 14,
	color: "#2A2A2A",
	background: "#FFFCF8",
	outline: "none",
	width: "100%",
	boxSizing: "border-box",
	fontFamily: "inherit",
};

// ── Modal ─────────────────────────────────────────────────────────
const ProductFormModal = ({
	title,
	form,
	onChange,
	onSubmit,
	onClose,
	submitting,
	imagePreview,
	onImageChange,
	submitLabel,
	categories,
}) => (
	<div
		style={{
			position: "fixed",
			inset: 0,
			zIndex: 50,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			background: "rgba(0,0,0,0.45)",
			backdropFilter: "blur(3px)",
			padding: "24px 16px",
		}}
		onClick={onClose}
	>
		<div
			style={{
				width: "100%",
				maxWidth: 680,
				maxHeight: "90vh",
				overflowY: "auto",
				background: "#FFFCF8",
				borderRadius: 20,
				border: "0.5px solid #E6E1D6",
				boxShadow: "0 24px 64px rgba(0,0,0,0.16)",
				display: "flex",
				flexDirection: "column",
			}}
			onClick={(e) => e.stopPropagation()}
		>
			{/* Modal header */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: "18px 24px",
					borderBottom: "0.5px solid #E6E1D6",
					position: "sticky",
					top: 0,
					background: "#FFFCF8",
					zIndex: 10,
					borderRadius: "20px 20px 0 0",
				}}
			>
				<div>
					<h2
						style={{
							margin: 0,
							fontSize: 18,
							fontWeight: 600,
							color: "#2A2A2A",
						}}
					>
						{title}
					</h2>
					<p style={{ margin: "2px 0 0", fontSize: 13, color: "#8A857D" }}>
						{submitLabel === "Créer"
							? "Remplissez les informations du nouveau produit"
							: "Modifiez les informations du produit"}
					</p>
				</div>
				<button
					type="button"
					onClick={onClose}
					disabled={submitting}
					style={{
						width: 36,
						height: 36,
						borderRadius: "50%",
						border: "0.5px solid #E6E1D6",
						background: "transparent",
						color: "#6B6B6B",
						cursor: "pointer",
						fontSize: 18,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}
				>
					✕
				</button>
			</div>

			{/* Modal body */}
			<form
				onSubmit={onSubmit}
				style={{
					padding: "24px",
					display: "flex",
					flexDirection: "column",
					gap: 20,
				}}
			>
				{/* Image + name row */}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "160px minmax(0,1fr)",
						gap: 20,
					}}
				>
					{/* Image zone */}
					<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
						<label
							style={{
								fontSize: 12,
								fontWeight: 500,
								color: "#6B6B6B",
								letterSpacing: "0.04em",
							}}
						>
							Image
						</label>
						{imagePreview ? (
							<img
								src={imagePreview}
								style={{
									width: "100%",
									height: 160,
									objectFit: "cover",
									borderRadius: 12,
									border: "0.5px solid #E6E1D6",
									display: "block",
								}}
								alt="preview"
							/>
						) : (
							<div
								style={{
									height: 160,
									border: "1.5px dashed #D3D1C7",
									borderRadius: 12,
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									gap: 6,
									color: "#B4B2A9",
									fontSize: 13,
									cursor: "pointer",
								}}
							>
								<span style={{ fontSize: 28 }}>📷</span>
								<span>Choisir une image</span>
							</div>
						)}
						<input
							type="file"
							accept="image/*"
							onChange={onImageChange}
							style={{ fontSize: 12, color: "#6B6B6B" }}
						/>
						<p style={{ margin: 0, fontSize: 11, color: "#B4B2A9" }}>
							JPG, PNG — max 5 Mo
						</p>
					</div>

					{/* Right fields */}
					<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
						<Field label="Nom du produit" required>
							<input
								name="name"
								value={form.name}
								onChange={onChange}
								placeholder="ex: Espresso Signature"
								style={inputStyle}
								required
							/>
						</Field>

						<Field label="Slug (URL)">
							<input
								name="slug"
								value={form.slug}
								onChange={onChange}
								placeholder="ex: espresso-signature"
								style={{ ...inputStyle, fontFamily: "monospace", fontSize: 13 }}
							/>
						</Field>

						<Field label="Catégorie" required>
							<select
								name="category_id"
								value={form.category_id}
								onChange={onChange}
								style={inputStyle}
								required
							>
								<option value="">Sélectionner une catégorie</option>
								{categories.map((c) => (
									<option key={c.id} value={c.id}>
										{c.name}
									</option>
								))}
							</select>
						</Field>
					</div>
				</div>

				{/* Description */}
				<Field label="Description courte">
					<input
						name="short_description"
						value={form.short_description || ""}
						onChange={onChange}
						placeholder="Résumé affiché sur les cartes produit"
						style={inputStyle}
					/>
				</Field>

				<Field label="Description complète">
					<textarea
						name="description"
						value={form.description}
						onChange={onChange}
						placeholder="Description détaillée du produit..."
						rows={3}
						style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }}
					/>
				</Field>

				{/* Prix + Stock */}
				<div
					style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
				>
					<Field label="Prix (CHF)" required>
						<input
							name="price"
							type="number"
							step="0.05"
							min="0"
							value={form.price}
							onChange={onChange}
							placeholder="0.00"
							style={inputStyle}
							required
						/>
					</Field>
					<Field label="Stock">
						<input
							name="stock"
							type="number"
							min="0"
							value={form.stock}
							onChange={onChange}
							placeholder="100"
							style={inputStyle}
						/>
					</Field>
				</div>

				{/* Toggles */}
				<div
					style={{
						display: "flex",
						gap: 24,
						padding: "12px 16px",
						background: "#F5F3EE",
						borderRadius: 10,
					}}
				>
					<label
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							fontSize: 14,
							color: "#2A2A2A",
							cursor: "pointer",
						}}
					>
						<input
							type="checkbox"
							name="available"
							checked={form.available}
							onChange={onChange}
						/>
						Produit actif
					</label>
					<label
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							fontSize: 14,
							color: "#2A2A2A",
							cursor: "pointer",
						}}
					>
						<input
							type="checkbox"
							name="featured"
							checked={form.featured || false}
							onChange={onChange}
						/>
						Mise en avant (vedette)
					</label>
				</div>

				{/* Footer */}
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 10,
						paddingTop: 8,
						borderTop: "0.5px solid #E6E1D6",
					}}
				>
					<button
						type="button"
						onClick={onClose}
						disabled={submitting}
						style={{
							padding: "9px 20px",
							borderRadius: 10,
							border: "0.5px solid #D3D1C7",
							background: "transparent",
							color: "#6B6B6B",
							fontSize: 14,
							cursor: "pointer",
							fontFamily: "inherit",
						}}
					>
						Annuler
					</button>
					<button
						type="submit"
						disabled={submitting}
						style={{
							padding: "9px 24px",
							borderRadius: 10,
							border: "none",
							background: "#2A2A2A",
							color: "#FFFCF8",
							fontSize: 14,
							fontWeight: 600,
							cursor: submitting ? "not-allowed" : "pointer",
							opacity: submitting ? 0.6 : 1,
							fontFamily: "inherit",
						}}
					>
						{submitting ? "Enregistrement..." : submitLabel}
					</button>
				</div>
			</form>
		</div>
	</div>
);

// ── Main page ─────────────────────────────────────────────────────
const AdminProductsPage = () => {
	const [products, setProducts] = useState([]);
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(null);
	const [editOpen, setEditOpen] = useState(false);
	const [createOpen, setCreateOpen] = useState(false);
	const [form, setForm] = useState(initialFormState);
	const [editId, setEditId] = useState(null);
	const [imageFile, setImageFile] = useState(null);
	const [preview, setPreview] = useState("");

	useEffect(() => {
		load();
	}, []);

	const load = async () => {
		try {
			setLoading(true);
			const [p, c] = await Promise.all([getAdminProducts(), getCategories()]);
			setProducts(p || []);
			setCategories(c || []);
		} finally {
			setLoading(false);
		}
	};

	const onChange = (e) => {
		const { name, value, type, checked } = e.target;

		setForm((prev) => {
			const updated = {
				...prev,
				[name]: type === "checkbox" ? checked : value,
			};
			// Auto-slug quand on tape le nom et que le slug est vide ou auto-généré
			if (name === "name" && (!prev.slug || prev.slug === toSlug(prev.name))) {
				updated.slug = toSlug(value);
			}
			return updated;
		});
	};

	const onImage = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setImageFile(file);
		setPreview(URL.createObjectURL(file));
	};

	const save = async (e) => {
		e.preventDefault();
		setSaving(true);
		try {
			let payload = {
				...form,
				price: Number(form.price || 0),
				stock: form.stock === "" ? null : Number(form.stock),
			};

			let product;
			if (editId) {
				product = await updateProductById(editId, payload);
			} else {
				product = await createProduct(payload);
			}

			if (imageFile && product?.id) {
				const url = await uploadProductImage(imageFile, product.id);
				await updateProductById(product.id, { image_url: url });
			}

			await load();
			// ✅ Invalider le cache pour forcer le rechargement
			sessionStorage.removeItem("menu_products");
			sessionStorage.removeItem("menu_products_at");
			sessionStorage.removeItem("featured_products");
			sessionStorage.removeItem("featured_products_at");
			close();
		} finally {
			setSaving(false);
		}
	};

	const remove = async (id) => {
		if (!confirm("Supprimer ce produit définitivement ?")) return;
		setDeleting(id);
		try {
			await deleteProductById(id);
			setProducts((p) => p.filter((x) => x.id !== id));

			// ✅ Invalider le cache
			sessionStorage.removeItem("menu_products");
			sessionStorage.removeItem("menu_products_at");
			sessionStorage.removeItem("featured_products");
			sessionStorage.removeItem("featured_products_at");
		} finally {
			setDeleting(null);
		}
	};

	const openEdit = (p) => {
		setEditId(p.id);
		setForm({
			name: p.name || "",
			slug: p.slug || "",
			category_id: p.category_id || "",
			short_description: p.short_description || "",
			description: p.description || "",
			price: p.price ?? "",
			stock: p.stock ?? "",
			available: p.available ?? true,
			featured: p.featured || false,
			image_url: p.image_url || "",
		});
		setPreview(p.image_url || "");
		setEditOpen(true);
	};

	const openCreate = () => {
		setEditId(null);
		setForm(initialFormState);
		setPreview("");
		setImageFile(null);
		setCreateOpen(true);
	};

	const close = () => {
		setEditOpen(false);
		setCreateOpen(false);
		setEditId(null);
		setForm(initialFormState);
		setImageFile(null);
		setPreview("");
	};

	const formatPrice = (price) =>
		new Intl.NumberFormat("fr-CH", {
			style: "currency",
			currency: "CHF",
		}).format(Number(price || 0));

	return (
		<div
			style={{ maxWidth: 1280, margin: "0 auto", padding: "128px 24px 48px" }}
		>
			{/* Header */}
			<div
				style={{
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "space-between",
					marginBottom: 32,
				}}
			>
				<div>
					<h1
						style={{
							margin: 0,
							fontSize: 28,
							fontWeight: 700,
							color: "#2A2A2A",
						}}
					>
						Produits
					</h1>
					<p style={{ margin: "6px 0 0", fontSize: 15, color: "#8A857D" }}>
						{products.length} produit{products.length !== 1 ? "s" : ""} dans le
						catalogue
					</p>
				</div>
				<button
					onClick={openCreate}
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 8,
						padding: "10px 20px",
						background: "#2A2A2A",
						color: "#FFFCF8",
						border: "none",
						borderRadius: 12,
						fontSize: 14,
						fontWeight: 600,
						cursor: "pointer",
						fontFamily: "inherit",
					}}
				>
					<span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
					Ajouter un produit
				</button>
			</div>

			{/* Table */}
			{loading ? (
				<div
					style={{
						padding: "48px 0",
						textAlign: "center",
						color: "#8A857D",
						fontSize: 15,
					}}
				>
					Chargement des produits...
				</div>
			) : products.length === 0 ? (
				<div
					style={{
						padding: "64px 24px",
						textAlign: "center",
						background: "#FFFCF8",
						border: "0.5px solid #E6E1D6",
						borderRadius: 16,
						color: "#8A857D",
					}}
				>
					<p style={{ fontSize: 32, margin: "0 0 12px" }}>☕</p>
					<p
						style={{
							margin: 0,
							fontSize: 16,
							fontWeight: 500,
							color: "#2A2A2A",
						}}
					>
						Aucun produit
					</p>
					<p style={{ margin: "6px 0 0", fontSize: 14 }}>
						Commencez par ajouter votre premier produit.
					</p>
				</div>
			) : (
				<div
					style={{
						background: "#FFFCF8",
						border: "0.5px solid #E6E1D6",
						borderRadius: 16,
						overflow: "hidden",
					}}
				>
					<table
						style={{
							width: "100%",
							borderCollapse: "collapse",
							tableLayout: "fixed",
						}}
					>
						<colgroup>
							<col style={{ width: 56 }} />
							<col style={{ width: "28%" }} />
							<col style={{ width: "16%" }} />
							<col style={{ width: "11%" }} />
							<col style={{ width: "9%" }} />
							<col style={{ width: "14%" }} />
							<col style={{ width: 100 }} />
						</colgroup>
						<thead>
							<tr
								style={{
									background: "#F5F3EE",
									borderBottom: "0.5px solid #E6E1D6",
								}}
							>
								{[
									"",
									"Produit",
									"Catégorie",
									"Prix",
									"Stock",
									"Statut",
									"",
								].map((h, i) => (
									<th
										key={i}
										style={{
											padding: "11px 14px",
											textAlign: "left",
											fontSize: 11,
											fontWeight: 600,
											color: "#8A857D",
											letterSpacing: "0.1em",
											textTransform: "uppercase",
										}}
									>
										{h}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{products.map((p, index) => (
								<tr
									key={p.id}
									style={{
										borderBottom:
											index < products.length - 1
												? "0.5px solid #F1ECE4"
												: "none",
										transition: "background 0.15s",
									}}
									onMouseEnter={(e) =>
										(e.currentTarget.style.background = "#FAF8F4")
									}
									onMouseLeave={(e) =>
										(e.currentTarget.style.background = "transparent")
									}
								>
									<td style={{ padding: "12px 14px" }}>
										<ProductThumb src={p.image_url} alt={p.name} />
									</td>
									<td style={{ padding: "12px 14px" }}>
										<p
											style={{
												margin: 0,
												fontWeight: 600,
												fontSize: 14,
												color: "#2A2A2A",
											}}
										>
											{p.name}
										</p>
										<p
											style={{
												margin: "2px 0 0",
												fontSize: 12,
												color: "#B4B2A9",
												fontFamily: "monospace",
											}}
										>
											{p.slug}
										</p>
									</td>
									<td style={{ padding: "12px 14px" }}>
										<span
											style={{
												display: "inline-block",
												padding: "3px 10px",
												borderRadius: 20,
												fontSize: 12,
												fontWeight: 500,
												background: "#F1ECE4",
												color: "#6B6B6B",
											}}
										>
											{p.categories?.name || "—"}
										</span>
									</td>
									<td
										style={{
											padding: "12px 14px",
											fontSize: 14,
											fontWeight: 600,
											color: "#2A2A2A",
										}}
									>
										{formatPrice(p.price)}
									</td>
									<td
										style={{
											padding: "12px 14px",
											fontSize: 14,
											color: p.stock === 0 ? "#E24B4A" : "#2A2A2A",
										}}
									>
										{p.stock ?? "—"}
									</td>
									<td style={{ padding: "12px 14px" }}>
										<StatusBadge available={p.available} stock={p.stock} />
									</td>
									<td style={{ padding: "12px 14px" }}>
										<div style={{ display: "flex", gap: 6 }}>
											<button
												onClick={() => openEdit(p)}
												title="Modifier"
												style={{
													padding: "6px 10px",
													borderRadius: 8,
													border: "0.5px solid #D3D1C7",
													background: "transparent",
													color: "#6B6B6B",
													cursor: "pointer",
													fontSize: 13,
													fontFamily: "inherit",
												}}
											>
												✏️
											</button>
											<button
												onClick={() => remove(p.id)}
												title="Supprimer"
												disabled={deleting === p.id}
												style={{
													padding: "6px 10px",
													borderRadius: 8,
													border: "0.5px solid #F7C1C1",
													background: "transparent",
													color: "#A32D2D",
													cursor: deleting === p.id ? "not-allowed" : "pointer",
													fontSize: 13,
													opacity: deleting === p.id ? 0.5 : 1,
													fontFamily: "inherit",
												}}
											>
												{deleting === p.id ? "..." : "🗑"}
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{/* Modal */}
			{(editOpen || createOpen) && (
				<ProductFormModal
					title={editOpen ? "Modifier le produit" : "Ajouter un produit"}
					form={form}
					onChange={onChange}
					onSubmit={save}
					onClose={close}
					submitting={saving}
					imagePreview={preview}
					onImageChange={onImage}
					submitLabel={editOpen ? "Enregistrer" : "Créer"}
					categories={categories}
				/>
			)}
		</div>
	);
};

export default AdminProductsPage;
